import React from "react";
import { ChangeEventHandler, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";

import Footer from "../components/Footer";
import DFNavbar from "../components/Navbar";
import { MANAGEMENT_USER_LICENSE_SEARCH, MYPAGE_ROUTE } from "../consts/routes";
import { useUserContext } from "../contexts/UserContext";
import { CrossCircleIcon } from "../components/Icons";
import { ScrollToTop } from "../components/ScrollToTop";
import { User } from "../types/User";
import i18n from "../i18n/configs";
import { OnlyMasterUserAuthorized } from "../components/OnlyMasterUserAuthorized";
import { formatDateTimeJAFormat, formatUtcToLocalDateTime } from "../utils/DateUtils";
import { parseStringToInt } from "../utils/ValidateUtils";
import { Product } from "../types/Product";
import { getProducts } from "../clients/ProductClient";
import { PaymentHistoryWithLicenses } from "../types/PaymentHistoryWithLicenses";
import { checkoutWithoutPayment } from "../clients/PaymentClient";
import { classOptions, licenseOptions, productTypeOptions } from "../consts/constants";
import MyDatePicker from "../components/MyDatePicker";
import { calcExpirationDate } from "../utils/LicenseUtils";

interface LocationState {
    selectedUser: User;
}

/**
 * 管理者向け - ライセンス新規発行画面
 * @returns JSX.Element
 */
const ManagementUserLicenseIssue = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, getRecentToken } = useUserContext();
    const location = useLocation();
    const { selectedUser } = location.state as LocationState;
    const [errorMessage, setErrorMessage] = useState<string>("");

    // ライセンス情報
    const [selectedProductType, setSelectedProductType] = useState<number>(11);
    const [selectedClass, setSelectedClass] = useState<number | undefined>(undefined);
    const [selectedLicenseType, setSelectedLicenseType] = useState<number | undefined>(undefined);
    const [selectedActivationDate, setSelectedActivationDate] = useState<Date | null>(
        new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 0, 0, 0, 0),
    );
    const [selectedExpirationDate, setSelectedExpirationDate] = useState<Date | null>(
        new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 0, 0, 0, 0),
    );
    const [issuedLicense, setIssuedLicense] = useState<PaymentHistoryWithLicenses | undefined>();

    // ページ制御
    const [disableExpirationDate, setDisableExpirationDate] = useState<boolean>(false);
    const [issueCompleted, setIssueCompleted] = useState<boolean>(false);

    /**
     * 処理中フラグ
     * ※多重クリックを防止するため、外部通信を伴うイベント処理では必ず使用すること
     */
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    // ページ表示時にuseEffectが2回実行されるのを防ぐために使用。
    // ※React.StrictModeの仕様
    const effectRun = useRef(false);

    // ページ初期化処理（DBから情報の取得）
    useEffect(() => {
        if (!user) {
            // ページリロード時はコンテキストも初期化されるため、空の場合は一度リターンする
            // コンテキストが生成されユーザー情報が生成された時点で再実行する
            return;
        }
        if (!effectRun.current) {
            return () => {
                effectRun.current = true;
            };
        }
    }, [user]);

    /**
     * ライセンスタイプ・利用開始日時を変更したときに有効期限を自動計算するフック処理
     */
    useEffect(() => {
        if (selectedLicenseType != undefined && selectedActivationDate) {
            // 有効期限を計算してセット
            const exp = calcExpirationDate(selectedLicenseType, selectedActivationDate);
            exp && setSelectedExpirationDate(exp);

            if ([300, 301, 302].includes(selectedLicenseType)) {
                setDisableExpirationDate(true); // 有効期限編集不可（恒久版）
            } else {
                setDisableExpirationDate(false); // 有効期限編集可
            }
        }
    }, [selectedActivationDate, selectedLicenseType]);

    /** 製品タイプ選択イベント */
    const handleSelectProductType: ChangeEventHandler<HTMLSelectElement> = (event) => {
        setSelectedProductType(parseStringToInt(event.target.value));
        setSelectedClass(undefined);
        setSelectedLicenseType(undefined);
    };

    /** ライセンス区分選択イベント */
    const handleSelectClass: ChangeEventHandler<HTMLSelectElement> = (event) => {
        event.target.value == "" ? setSelectedClass(undefined) : setSelectedClass(parseStringToInt(event.target.value));
        setSelectedLicenseType(undefined);
    };

    /** ライセンスタイプ選択イベント */
    const handleSelectLicenseType: ChangeEventHandler<HTMLSelectElement> = (event) => {
        event.target.value == ""
            ? setSelectedLicenseType(undefined)
            : setSelectedLicenseType(parseStringToInt(event.target.value));
    };

    /**
     * ライセンス発行押下時のイベント処理
     * @param event
     */
    const handleIssueLicense = async () => {
        setErrorMessage("");
        setIsProcessing(true);
        try {
            // 利用開始日と有効期限日時のチェック
            if (!disableExpirationDate) {
                if (
                    selectedExpirationDate &&
                    selectedActivationDate &&
                    selectedExpirationDate?.getTime() < selectedActivationDate?.getTime()
                ) {
                    setErrorMessage("有効期限が利用開始日時より前になっています。");
                    return;
                }
            }

            // トークンチェック&取得
            const idToken = await getRecentToken();
            if (!idToken) {
                setErrorMessage(t("ErrorMessage.failToGetIdpToken"));
                return;
            }
            // [API]商品情報一覧取得
            const agencyId = 0;
            const productList: Product[] = await getProducts(idToken, selectedProductType, agencyId);

            // ライセンスタイプが一致する商品を抽出
            const targetProduct: Product | undefined = productList.find(
                (p) => p.license_type_id == selectedLicenseType,
            );
            if (targetProduct == undefined) {
                setErrorMessage("対応する商品情報が見つかりませんでした。");
                return;
            }

            // [API]Checkout API
            const productId: number = targetProduct.product_id;
            const issuedResult: PaymentHistoryWithLicenses = await checkoutWithoutPayment(
                idToken,
                productId,
                selectedUser.email,
                1, // 現状は1固定
                selectedActivationDate?.toISOString(),
                selectedExpirationDate?.toISOString(),
            );
            setIssuedLicense(issuedResult);
            setIssueCompleted(true);
        } catch (error: any) {
            setErrorMessage(t("ErrorMessage.systemErrorMessage"));
        } finally {
            setIsProcessing(false);
        }
    };

    /**
     * 選択内容をすべてリセット
     */
    const handleReset = async () => {
        setIssueCompleted(false);
        setIssuedLicense(undefined);
        setSelectedClass(undefined);
        setSelectedLicenseType(undefined);
        window.scrollTo(0, 0); // 最上部へスクロール
    };

    return (
        <div className="flex min-h-screen flex-col">
            <Helmet title="ライセンス新規発行 | DF WebService" />
            <ScrollToTop />
            {/* マスターユーザーのみ閲覧可 */}
            <OnlyMasterUserAuthorized />
            <DFNavbar bottomPadding={true} />
            <div className="flex-grow items-center justify-center px-6 py-8 pt-20">
                <h1 className="h1-common py-3">ライセンス新規発行</h1>
                <div className="mx-auto max-w-3xl">
                    <div className="m-2 grid grid-cols-1 items-center justify-center gap-4 pb-6 pt-2 sm:grid-cols-3">
                        {/* 製品タイプ */}
                        <div className="col-span-1 py-1">
                            <div className="py-2 text-sm font-bold text-gray-800">製品タイプ</div>
                            <select
                                id="selectProductType"
                                value={selectedProductType}
                                onChange={handleSelectProductType}
                                className="w-[180px] rounded-md border border-gray-300 bg-gray-100 p-2 text-sm"
                                disabled={issueCompleted}
                            >
                                {productTypeOptions.map((p) => (
                                    <option key={p.productTypeId} value={p.productTypeId}>
                                        {p.productTypeName}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* ライセンス区分 */}
                        <div className="col-span-1 py-1">
                            <div className="py-2 text-sm font-bold text-gray-800">ライセンス区分</div>
                            <select
                                id="selectClass"
                                value={selectedClass ?? ""}
                                onChange={handleSelectClass}
                                className="w-[180px] rounded-md border border-gray-300 bg-gray-100 p-2 text-sm"
                                disabled={issueCompleted}
                            >
                                <option value="">未選択</option>
                                {(selectedProductType == 41 // DF BIRDの場合は林業事業体・教育研究機関は無し
                                    ? classOptions.filter((c) => c.classId !== 2 && c.classId !== 3)
                                    : classOptions
                                ).map((c) => (
                                    <option key={c.classId} value={c.classId}>
                                        {c.className}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* ライセンスタイプ */}
                        <div className="col-span-1 py-1">
                            <div className="py-2 text-sm font-bold text-gray-800">ライセンスタイプ</div>
                            <select
                                id="selectLicenseType"
                                value={selectedLicenseType == undefined ? "" : selectedLicenseType}
                                onChange={handleSelectLicenseType}
                                className="w-[180px] rounded-md border border-gray-300 bg-gray-100 p-2 text-sm"
                                disabled={issueCompleted}
                            >
                                <option value="">未選択</option>
                                {licenseOptions
                                    .filter((l) => l.classId == selectedClass)
                                    .map((l) => (
                                        <option key={l.licenseTypeId} value={l.licenseTypeId}>
                                            {l.labelName}
                                        </option>
                                    ))}
                            </select>
                        </div>

                        {/* ライセンス利用開始日時 */}
                        <div className="col-span-1 py-1">
                            <div className="py-2 text-sm font-bold text-gray-800">利用開始日時</div>
                            <MyDatePicker
                                selectedDate={selectedActivationDate}
                                setDate={setSelectedActivationDate}
                                placeholderText="-"
                                disabled={issueCompleted}
                            />
                        </div>

                        {/* ライセンス有効期限日時 */}
                        <div className="col-span-1 py-1">
                            <div className="py-2 text-sm font-bold text-gray-800">有効期限日時</div>
                            <MyDatePicker
                                selectedDate={selectedExpirationDate}
                                setDate={setSelectedExpirationDate}
                                placeholderText="-"
                                disabled={disableExpirationDate || issueCompleted}
                            />
                        </div>
                    </div>
                </div>
                <hr className="mb-8 mt-2 border-b border-t-2" />

                {/* エラーメッセージ */}
                {errorMessage && (
                    <div className="flex items-center justify-center pb-4">
                        <CrossCircleIcon />
                        <span className="pl-1 text-center font-bold text-red-600">
                            {errorMessage.split("\n").map((line, index) => (
                                <div key={index}>{line}</div>
                            ))}
                        </span>
                    </div>
                )}

                {/* ライセンス発行前の状態 */}
                {!issueCompleted && (
                    <>
                        {/* 発行内容確認パネル */}
                        <div className="mx-auto flex max-w-2xl items-center justify-center font-bold">
                            <div className="min-w-full rounded-lg border border-gray-200 bg-yellow-50 p-2 shadow">
                                <div className="pl-2 pt-2 text-gray-700">以下の内容でライセンスを発行します</div>

                                <div className="grid grid-cols-1 items-center justify-center py-2 sm:grid-cols-4">
                                    {/* DFアカウント */}
                                    <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">DF アカウント</div>
                                    <div className="col-span-3 py-1">{selectedUser.email}</div>
                                    {/* 商品名 */}
                                    <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">製品タイプ</div>
                                    <div className="col-span-3 py-1">
                                        {
                                            productTypeOptions.find(
                                                (option) => option.productTypeId === selectedProductType,
                                            )?.productTypeName
                                        }
                                    </div>
                                    {/* ライセンス */}
                                    <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">ライセンスタイプ</div>
                                    <div className="col-span-3 py-1">
                                        {selectedLicenseType != undefined ? (
                                            licenseOptions
                                                .filter((l) => l.classId == selectedClass)
                                                .find((l) => l.licenseTypeId == selectedLicenseType)?.licenseTypeName
                                        ) : (
                                            <span className="text-gray-500">== 未選択 ==</span>
                                        )}
                                    </div>
                                    {/* 利用開始日時 */}
                                    <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">利用開始日時</div>
                                    <div className="col-span-3 py-1">
                                        {selectedActivationDate && formatDateTimeJAFormat(selectedActivationDate)}
                                    </div>
                                    {/* 有効期限日時 */}
                                    {!disableExpirationDate && (
                                        <>
                                            <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">
                                                有効期限日時
                                            </div>
                                            <div className="col-span-3 py-1">
                                                {selectedExpirationDate &&
                                                    formatDateTimeJAFormat(selectedExpirationDate)}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ライセンスを発行 */}
                        <div className="mt-4 flex items-center justify-evenly">
                            <button
                                type="button"
                                onClick={handleIssueLicense}
                                className={`${
                                    isProcessing || selectedLicenseType == undefined
                                        ? "btn-primary-disabled"
                                        : "btn-primary"
                                } w-[200px] py-2`}
                                disabled={isProcessing || selectedLicenseType == undefined}
                            >
                                {isProcessing ? (
                                    <div className="flex items-center justify-center">
                                        <div className="mr-1 h-4 w-4 animate-spin rounded-full border-2 border-lime-100 border-t-transparent"></div>
                                        <label className="">Processing...</label>
                                    </div>
                                ) : (
                                    "ライセンスを発行"
                                )}
                            </button>
                        </div>
                    </>
                )}

                {/* ライセンス発行完了状態 */}
                {issueCompleted && (
                    <>
                        {/* 発行結果表示パネル */}
                        <div className="mx-auto flex max-w-2xl items-center justify-center font-bold">
                            <div className="min-w-full rounded-lg border border-gray-200 bg-green-50 p-2 shadow">
                                <div className="pl-2 pt-2 text-green-600">ライセンスの発行が完了しました</div>

                                <div className="grid grid-cols-4 items-center justify-center py-2">
                                    {/* DFアカウント */}
                                    <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">DF アカウント</div>
                                    <div className="col-span-3 py-1">{issuedLicense?.p_email}</div>
                                    {/* 氏名 */}
                                    <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">氏名</div>
                                    <div className="col-span-3 py-1">{issuedLicense?.p_name}</div>
                                    {/* 所属 */}
                                    <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">所属</div>
                                    <div className="col-span-3 py-1">{issuedLicense?.p_organization}</div>
                                    {/* 商品名 */}
                                    <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">商品名</div>
                                    <div className="col-span-3 py-1">
                                        {i18n.language === "ja"
                                            ? issuedLicense?.p_product_type_name.split("%%")[0]
                                            : issuedLicense?.p_product_type_name.split("%%")[1]}
                                    </div>
                                    {issuedLicense?.licenses.map((license, index) => (
                                        <React.Fragment key={index}>
                                            <div className="col-span-4 py-1 pl-4 text-sm text-gray-500">
                                                == ライセンス情報 {index + 1} ==
                                            </div>
                                            {/* ライセンスキー */}
                                            <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">
                                                ライセンスキー
                                            </div>
                                            <div className="col-span-3 py-1">{license.license_key}</div>
                                            {/* 利用開始日時 */}
                                            <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">
                                                利用開始日時
                                            </div>
                                            <div className="col-span-3 py-1">
                                                {formatUtcToLocalDateTime(license.activation_date)}
                                            </div>
                                            {/* 有効期限日時 */}
                                            {!license.permanent_flg && (
                                                <>
                                                    <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">
                                                        有効期限日時
                                                    </div>
                                                    <div className="col-span-3 py-1">
                                                        {formatUtcToLocalDateTime(license.expire_date)}
                                                    </div>
                                                </>
                                            )}
                                            {/* サポート開始日時 */}
                                            {license.permanent_flg && (
                                                <>
                                                    <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">
                                                        サポート開始日時
                                                    </div>
                                                    <div className="col-span-3 py-1">
                                                        {formatUtcToLocalDateTime(license.support_start_date)}
                                                    </div>
                                                </>
                                            )}
                                            {/* サポート有効期限 */}
                                            {license.permanent_flg && (
                                                <>
                                                    <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">
                                                        サポート有効期限
                                                    </div>
                                                    <div className="col-span-3 py-1">
                                                        {formatUtcToLocalDateTime(license.support_expiration_date)}
                                                    </div>
                                                </>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* 続けて他のライセンスを発行する */}
                        <div className="mt-4 flex items-center justify-evenly">
                            <button
                                type="button"
                                onClick={handleReset}
                                className={`${
                                    isProcessing ? "btn-primary-disabled" : "btn-primary"
                                } w-[240px] py-2 text-sm`}
                                disabled={isProcessing}
                            >
                                {isProcessing ? (
                                    <div className="flex items-center justify-center">
                                        <div className="mr-1 h-4 w-4 animate-spin rounded-full border-2 border-lime-100 border-t-transparent"></div>
                                        <label className="">Processing...</label>
                                    </div>
                                ) : (
                                    "続けて他のライセンスを発行する"
                                )}
                            </button>
                        </div>
                    </>
                )}

                {/* ライセンス確認画面へ戻る */}
                <div className="flex items-center justify-center">
                    <div className="grid max-w-md grid-cols-2 gap-6 py-6 sm:m-2">
                        {/* ライセンス確認画面へ戻る */}
                        <div className="col-span-2 mx-auto max-w-xl items-center justify-evenly sm:col-span-1">
                            <button
                                type="button"
                                onClick={() =>
                                    navigate(MANAGEMENT_USER_LICENSE_SEARCH, { state: { selectedUser: selectedUser } })
                                }
                                className="btn-secondary mx-2 w-[200px]"
                            >
                                ライセンス確認画面へ戻る
                            </button>
                        </div>
                        {/* マイメニューへ戻る */}
                        <div className="col-span-2 mx-auto max-w-xl items-center justify-evenly sm:col-span-1">
                            <button
                                type="button"
                                onClick={() => navigate(MYPAGE_ROUTE)}
                                className="btn-secondary mx-2 w-[200px]"
                            >
                                マイメニューへ戻る
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};
export default ManagementUserLicenseIssue;
